/**
 * Route → breadcrumb-section registry.
 *
 * The shared `<Breadcrumbs>` bar derives a navigable trail from the
 * current pathname using this table. Each entry contributes one
 * clickable crumb whenever its `match` prefix matches the route, so
 * `/settings/integrations/slack` resolves to `Settings › Integrations`
 * with both roots clickable.
 *
 * Labels are i18n keys (resolved at render time), reusing the sidebar
 * `nav.*` keys wherever possible so a breadcrumb root reads identically
 * to the rail the operator clicked from — and stays correct in every
 * locale (no humanised English segments leaking into a Dutch UI).
 *
 * Detail pages (`/issues/[key]`, `/patches/[id]`, …) don't rely on the
 * registry for their entity crumb — they pass an explicit, return-aware
 * trail to `<Breadcrumbs items=…>`. The registry only powers the
 * automatic section trail every other page gets for free via PageHeader.
 */

export type SectionCrumb = {
  /** i18n key for the crumb label. */
  labelKey: string;
  /** Route the crumb links to. */
  href: string;
  /**
   * Prefix used to test the current pathname. Defaults to `href`.
   * Set when the section landing page differs from the subtree root —
   * Settings links to `/settings/infrastructure` but owns all of
   * `/settings/*`, so it matches on `/settings`.
   */
  match?: string;
};

/**
 * Ordered loosely parent-first for readability; `sectionTrail` sorts the
 * matches by prefix length so parents always render before children
 * regardless of declaration order.
 */
const SECTIONS: SectionCrumb[] = [
  { href: "/app", labelKey: "nav.dashboard" },
  { href: "/inbox", labelKey: "nav.inbox" },
  { href: "/notifications", labelKey: "nav.notifications" },
  { href: "/approvals", labelKey: "nav.approvals" },
  { href: "/issues", labelKey: "nav.issues" },
  // Missions and Customers were missing here, so their detail pages derived
  // an EMPTY trail and silently fell back to a bare eyebrow — no way back to
  // the list except the browser button.
  { href: "/missions", labelKey: "nav.missions" },
  { href: "/customers", labelKey: "nav.customerDirectory" },
  { href: "/projects", labelKey: "nav.projects" },
  { href: "/workspace", labelKey: "nav.workspaces" },
  { href: "/patches", labelKey: "nav.patches" },
  { href: "/sandbox-runs", labelKey: "nav.sandboxRuns" },
  { href: "/follow-ups", labelKey: "nav.followUps" },
  { href: "/knowledge", labelKey: "nav.knowledge" },
  { href: "/memory", labelKey: "nav.memory" },
  { href: "/agents", labelKey: "nav.aiTeam" },
  { href: "/orchestration", labelKey: "nav.orchestrationConsole" },
  { href: "/runs", labelKey: "nav.runs" },
  { href: "/tenants", labelKey: "nav.tenants" },
  // Settings + its common Integrations sub-root. Settings lights up
  // across the whole subtree but links back to its landing page.
  { href: "/settings/integrations", labelKey: "breadcrumb.integrations" },
  { href: "/settings/profile", match: "/settings", labelKey: "nav.settings" },
  // Platform-admin section. No bare `/admin` index, so the root crumb
  // points at Customers (the first admin landing) while matching all
  // of `/admin/*`.
  { href: "/admin/customers", match: "/admin", labelKey: "nav.fixcontrol" },
];

/**
 * Resolve the ordered section trail for a pathname. Returns crumbs from
 * the broadest section to the narrowest (e.g. `[Settings, Integrations]`
 * for `/settings/integrations/slack`). The page's own leaf crumb (the
 * current entity/sub-page) is appended by the renderer, not here.
 */
export function sectionTrail(pathname: string): SectionCrumb[] {
  const matches = SECTIONS.filter((s) => {
    const m = s.match ?? s.href;
    return pathname === m || pathname.startsWith(m + "/");
  });
  matches.sort((a, b) => (a.match ?? a.href).length - (b.match ?? b.href).length);
  return matches;
}

/** True when the pathname IS a section landing (its own root), not a child. */
export function isSectionLanding(pathname: string, crumb: SectionCrumb): boolean {
  const m = crumb.match ?? crumb.href;
  return pathname === crumb.href || pathname === m;
}
